import { ENV_CONFIG } from '../../../config/environment';
import { supabase } from '../../../lib/supabase';
import { PremiumFeature } from '../types/premiumFeatures';

import { entitlementService } from './entitlementService';

export interface DailyUsage {
  id: string;
  userId: string;
  date: string; // YYYY-MM-DD format
  quizCount: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface FreemiumLimits {
  dailyQuizLimit: number;
  currentUsage: number;
  remainingQuizzes: number;
  hasUnlimited: boolean;
  resetTime: Date; // When the limit resets (next day)
}

export class FreemiumLimitService {
  private static readonly DAILY_QUIZ_LIMIT = 7;
  private static tableExists: boolean | null = null;

  /**
   * Check if daily_quiz_usage table exists (simplified approach)
   */
  private async checkTableExists(): Promise<boolean> {
    // Cache the result to avoid repeated checks
    if (FreemiumLimitService.tableExists !== null) {
      return FreemiumLimitService.tableExists;
    }

    // Since we know the table exists in production, return true
    // This prevents unnecessary network requests and 406 errors
    FreemiumLimitService.tableExists = true;
    return true;
  }

  /**
   * Get the current daily quiz usage for a user
   */
  async getDailyUsage(userId: string): Promise<FreemiumLimits> {
    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

      // Check if user has unlimited access
      const hasUnlimited = await entitlementService.hasFeatureAccess(
        userId,
        PremiumFeature.UNLIMITED_DAILY_QUIZZES
      );

      if (hasUnlimited) {
        return {
          dailyQuizLimit: -1, // Unlimited
          currentUsage: 0,
          remainingQuizzes: -1, // Unlimited
          hasUnlimited: true,
          resetTime: this.getNextDayResetTime()
        };
      }

      // Check if table exists before making the request
      const tableExists = await this.checkTableExists();
      if (!tableExists) {
        // Return default limits without making a network request
        return {
          dailyQuizLimit: FreemiumLimitService.DAILY_QUIZ_LIMIT,
          currentUsage: 0,
          remainingQuizzes: FreemiumLimitService.DAILY_QUIZ_LIMIT,
          hasUnlimited: false,
          resetTime: this.getNextDayResetTime()
        };
      }

      // Get today's usage from database
      const { data: usage, error } = await supabase
        .from('daily_quiz_usage')
        .select('quiz_count')
        .eq('user_id', userId)
        .eq('date', today)
        .single();

      if (error) {
        // Handle specific error cases gracefully
        if (error.code === 'PGRST116') {
          // No rows returned - user hasn't played any quizzes today
          return {
            dailyQuizLimit: FreemiumLimitService.DAILY_QUIZ_LIMIT,
            currentUsage: 0,
            remainingQuizzes: FreemiumLimitService.DAILY_QUIZ_LIMIT,
            hasUnlimited: false,
            resetTime: this.getNextDayResetTime()
          };
        }

        // Table doesn't exist (406) or other database errors
        // Return default limits without logging error to console
        if (error.message?.includes('406') || error.code === '42P01') {
          // Silently handle missing table - this is expected in development
          // until the migration is run
          return {
            dailyQuizLimit: FreemiumLimitService.DAILY_QUIZ_LIMIT,
            currentUsage: 0,
            remainingQuizzes: FreemiumLimitService.DAILY_QUIZ_LIMIT,
            hasUnlimited: false,
            resetTime: this.getNextDayResetTime()
          };
        }

        // For other unexpected errors, log them
        console.error('Failed to get daily usage:', error);
        throw error;
      }

      const currentUsage = usage?.quiz_count || 0;
      const remainingQuizzes = Math.max(0, FreemiumLimitService.DAILY_QUIZ_LIMIT - currentUsage);

      return {
        dailyQuizLimit: FreemiumLimitService.DAILY_QUIZ_LIMIT,
        currentUsage,
        remainingQuizzes,
        hasUnlimited: false,
        resetTime: this.getNextDayResetTime()
      };
    } catch (error) {
      // Final catch for any unexpected errors
      console.error('Failed to get daily usage:', error);
      // Return default limits to prevent app crash
      return {
        dailyQuizLimit: FreemiumLimitService.DAILY_QUIZ_LIMIT,
        currentUsage: 0,
        remainingQuizzes: FreemiumLimitService.DAILY_QUIZ_LIMIT,
        hasUnlimited: false,
        resetTime: this.getNextDayResetTime()
      };
    }
  }

  /**
   * Check if user can start a new quiz
   */
  async canStartQuiz(userId: string): Promise<boolean> {
    try {
      const limits = await this.getDailyUsage(userId);
      
      if (limits.hasUnlimited) {
        return true;
      }

      return limits.remainingQuizzes > 0;
    } catch (error) {
      console.error('Failed to check quiz eligibility:', error);
      // Default to allowing quiz on error to avoid blocking users
      return true;
    }
  }

  /**
   * Record a completed quiz for usage tracking
   */
  async recordQuizCompletion(userId: string): Promise<void> {
    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

      // Check if user has unlimited access - if so, don't track usage
      const hasUnlimited = await entitlementService.hasFeatureAccess(
        userId,
        PremiumFeature.UNLIMITED_DAILY_QUIZZES
      );

      if (hasUnlimited) {
        return; // No need to track usage for unlimited users
      }

      // Check if table exists before making the request
      const tableExists = await this.checkTableExists();
      if (!tableExists) {
        // Silently return - no tracking needed when table doesn't exist
        return;
      }

      // Use upsert to increment or create usage record
      const { error } = await supabase
        .rpc('increment_daily_quiz_count', {
          p_user_id: userId,
          p_date: today
        });

      if (error) {
        console.error('Failed to record quiz completion:', error);
        throw error;
      }
    } catch (error) {
      console.error('Failed to record quiz completion:', error);
      throw error;
    }
  }

  /**
   * Get formatted limit message for UI display
   */
  async getLimitMessage(userId: string): Promise<string> {
    try {
      const limits = await this.getDailyUsage(userId);

      if (limits.hasUnlimited) {
        return "Unlimited daily quizzes ✨";
      }

      if (limits.remainingQuizzes > 0) {
        return `${limits.remainingQuizzes} quizzes remaining today`;
      } else {
        const resetTime = limits.resetTime.toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
        return `Great session! More quizzes at ${resetTime}`;
      }
    } catch (error) {
      console.error('Failed to get limit message:', error);
      return "Unable to check daily limit";
    }
  }

  /**
   * Get upgrade prompt message when limit is reached
   */
  getUpgradeMessage(currentUsage: number): string {
    return `You've answered ${currentUsage} quizzes today — wonderful! Unlock unlimited access to go even deeper.`;
  }

  /**
   * Reset usage for all users (called by daily cron job)
   */
  async resetDailyUsage(): Promise<void> {
    try {
      // Check if table exists before making the request
      const tableExists = await this.checkTableExists();
      if (!tableExists) {
        // Silently return - no reset needed when table doesn't exist
        return;
      }

      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];

      // Archive old usage data (keep for analytics but don't affect limits)
      const { error } = await supabase
        .from('daily_quiz_usage')
        .update({ archived: true })
        .lt('date', yesterdayStr);

      if (error) {
        console.error('Failed to reset daily usage:', error);
        throw error;
      }
    } catch (error) {
      console.error('Failed to reset daily usage:', error);
      throw error;
    }
  }

  /**
   * Get statistics for admin dashboard
   */
  async getUsageStats(): Promise<{
    totalUsers: number;
    premiumUsers: number;
    averageDailyQuizzes: number;
    limitReachedUsers: number;
  }> {
    try {
      // Check if table exists before making the request
      const tableExists = await this.checkTableExists();
      if (!tableExists) {
        // Return default stats when table doesn't exist
        return {
          totalUsers: 0,
          premiumUsers: 0,
          averageDailyQuizzes: 0,
          limitReachedUsers: 0
        };
      }

      const today = new Date().toISOString().split('T')[0];

      const { data: stats, error } = await supabase
        .rpc('get_daily_usage_stats', {
          p_date: today
        });

      if (error) {
        console.error('Failed to get usage stats:', error);
        throw error;
      }

      return stats || {
        totalUsers: 0,
        premiumUsers: 0,
        averageDailyQuizzes: 0,
        limitReachedUsers: 0
      };
    } catch (error) {
      console.error('Failed to get usage stats:', error);
      throw error;
    }
  }

  /**
   * Calculate the next day reset time (midnight)
   */
  private getNextDayResetTime(): Date {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0); // Set to midnight
    return tomorrow;
  }
}

// Singleton instance
export const freemiumLimitService = new FreemiumLimitService();

// Default export
export default freemiumLimitService;